import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Card } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import { IColumn, Table } from '@/core/components/Table'
import { RoadmapRank } from '@/common/models/gamification'
import { deleteRank, getRanks } from '@/core/services/gamification'

import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { translationName } from '../shared/labels'
import { RankEditorModal } from './RankEditorModal'

interface RankListProps {
  roadmapId: string
  hasEditPermission: boolean
  onError: (e: unknown) => void
}

/**
 * Manages a roadmap's ranks (points threshold + image + localized name). Self-fetches
 * from `GET /roadmaps/{roadmapId}/ranks` since ranks aren't nested on RoadmapDto.
 */
export const RankList = ({
  roadmapId,
  hasEditPermission,
  onError,
}: RankListProps) => {
  const [ranks, setRanks] = useState<RoadmapRank[]>([])
  const [loading, setLoading] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<RoadmapRank | null>(null)

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      const { data } = await getRanks(roadmapId)
      setRanks(data ?? [])
    } catch (e) {
      onError(e)
    } finally {
      setLoading(false)
    }
  }, [roadmapId, onError])

  useEffect(() => {
    refetch()
  }, [refetch])

  const orderedRanks = useMemo(
    () => [...ranks].sort((a, b) => a.pointsThreshold - b.pointsThreshold),
    [ranks],
  )

  const editingRank = editingId
    ? ranks.find((r) => r.id === editingId) ?? null
    : null

  const openCreate = () => {
    setEditingId(null)
    setEditorOpen(true)
  }

  const openEdit = (rank: RoadmapRank) => {
    if (!hasEditPermission) return
    setEditingId(rank.id)
    setEditorOpen(true)
  }

  const handleDelete = async (rank: RoadmapRank) => {
    try {
      await deleteRank(rank.id)
      await refetch()
    } catch (e) {
      onError(e)
    }
  }

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Threshold',
        accessor: 'pointsThreshold',
        render: (row: RoadmapRank) => (
          <span className="text-monochrome-1">{row.pointsThreshold}</span>
        ),
      },
      {
        title: 'Image',
        accessor: 'imageRef',
        render: (row: RoadmapRank) =>
          row.imageRef ? (
            <img
              src={row.imageRef}
              alt=""
              width={42}
              height={42}
              className="rounded-[8px] border border-monochrome-120 object-contain"
              style={{ width: 42, height: 42 }}
            />
          ) : (
            <span className="text-monochrome-2">—</span>
          ),
      },
      {
        title: 'Name',
        accessor: 'translations',
        render: (row: RoadmapRank) => (
          <span className="text-monochrome-1">
            {translationName(row.translations, '—')}
          </span>
        ),
      },
      {
        title: '',
        accessor: '',
        render: (row: RoadmapRank) => (
          <div className="flex items-center gap-4">
            <a
              className="text-accent-1 underline"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                openEdit(row)
              }}
            >
              Edit
            </a>
            {hasEditPermission && (
              <a
                className="text-accent-3 underline"
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setDeleting(row)
                }}
              >
                Delete
              </a>
            )}
          </div>
        ),
      },
    ],
    [hasEditPermission],
  )

  return (
    <Card className="p-5 !bg-monochrome-4-60">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-2xl font-bold text-monochrome-1">Ranks</div>
        {hasEditPermission && (
          <Button variant="primary-bordered" onClick={openCreate}>
            Add Rank
            <Icon
              icon="plus-circle"
              width="18"
              height="18"
              viewBox="0 0 18 18"
            />
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        data={orderedRanks}
        loading={loading}
        noDataMessage="No ranks yet"
      />

      <RankEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        roadmapId={roadmapId}
        rank={editingRank}
        onChanged={refetch}
        onError={onError}
      />

      <DeleteConfirmationModal
        showDeleteConfirmationModal={!!deleting}
        setDeleteConfirmationModal={(show) => {
          if (!show) setDeleting(null)
        }}
        deletedItem={
          deleting ? `rank at ${deleting.pointsThreshold} points` : undefined
        }
        deleteFunc={() => {
          if (deleting) handleDelete(deleting)
          setDeleting(null)
        }}
      />
    </Card>
  )
}
